import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

export interface CrmPipeline {
  id: string;
  courseId: string;
  kommoPipelineId: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SyncStatusSummary {
  synced: number;
  pending: number;
  noPhone: number;
}

export type KommoSyncState = 'synced' | 'no_phone' | 'no_prefix' | 'error' | 'pending';

export interface KommoSyncLog {
  status: string;
  reason: string | null;
  message: string;
  detail: string | null;
  statusCode: number | null;
  source: string;
  createdAt: string;
}

export interface KommoSyncRow {
  userId: string;
  name: string;
  lastname: string;
  email: string;
  state: KommoSyncState;
  lastLog: KommoSyncLog | null;
}

export interface KommoSyncSummary {
  synced: number;
  noPhone: number;
  noPrefix: number;
  errors: number;
  pending: number;
}

export type KommoJobStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface KommoJob {
  id: string;
  name: string;
  status: KommoJobStatus;
  output: Record<string, unknown> | null;
  startedAt: string | null;
  completedAt: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class KommoService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.apiBaseUrl}/crm`;

  validatePipeline(pipelineId: number): Observable<{ success: boolean; data: { valid: boolean } }> {
    return this.http.get<{ success: boolean; data: { valid: boolean } }>(`${this.baseUrl}/pipelines/validate/${pipelineId}`);
  }

  validateBot(botId: string): Observable<{ success: boolean; data: { valid: boolean } }> {
    return this.http.post<{ success: boolean; data: { valid: boolean } }>(`${this.baseUrl}/bots/validate/${botId}`, {});
  }

  saveCoursePipeline(courseId: string, pipelineId: number): Observable<{ success: boolean; data: CrmPipeline }> {
    return this.http.post<{ success: boolean; data: CrmPipeline }>(`${this.baseUrl}/courses/${courseId}/pipeline`, { pipelineId });
  }

  deleteCoursePipeline(courseId: string): Observable<{ success: boolean }> {
    return this.http.delete<{ success: boolean }>(`${this.baseUrl}/courses/${courseId}/pipeline`);
  }

  getCoursePipeline(courseId: string): Observable<{ success: boolean; data: CrmPipeline | null }> {
    return this.http.get<{ success: boolean; data: CrmPipeline | null }>(`${this.baseUrl}/courses/${courseId}/pipeline`);
  }

  syncUser(userId: string): Observable<{ success: boolean; data: { crmId: string } }> {
    return this.http.post<{ success: boolean; data: { crmId: string } }>(`${this.baseUrl}/users/${userId}/sync`, {});
  }

  syncAllUsers(includeAlreadySynced = false): Observable<{ success: boolean; data: { jobId: string } }> {
    return this.http.post<{ success: boolean; data: { jobId: string } }>(`${this.baseUrl}/users/sync-all`, { includeAlreadySynced });
  }

  getSyncStatus(): Observable<{ success: boolean; data: SyncStatusSummary }> {
    return this.http.get<{ success: boolean; data: SyncStatusSummary }>(`${this.baseUrl}/users/sync-status`);
  }

  getUsersSyncStatus(params: {
    page?: number;
    pageSize?: number;
    search?: string;
    status?: KommoSyncState[];
  }): Observable<{ success: boolean; data: { rows: KommoSyncRow[]; count: number; summary: KommoSyncSummary } }> {
    let httpParams = new HttpParams();
    if (params.page !== undefined) httpParams = httpParams.set('page', params.page);
    if (params.pageSize !== undefined) httpParams = httpParams.set('pageSize', params.pageSize);
    if (params.search) httpParams = httpParams.set('search', params.search);
    for (const status of params.status ?? []) {
      httpParams = httpParams.append('status', status);
    }

    return this.http.get<{ success: boolean; data: { rows: KommoSyncRow[]; count: number; summary: KommoSyncSummary } }>(
      `${this.baseUrl}/users/sync-status`,
      { params: httpParams }
    );
  }

  getUserSyncLog(
    userId: string,
    params: { page?: number; pageSize?: number }
  ): Observable<{ success: boolean; data: { rows: KommoSyncLog[]; count: number } }> {
    let httpParams = new HttpParams();
    if (params.page !== undefined) httpParams = httpParams.set('page', params.page);
    if (params.pageSize !== undefined) httpParams = httpParams.set('pageSize', params.pageSize);

    return this.http.get<{ success: boolean; data: { rows: KommoSyncLog[]; count: number } }>(`${this.baseUrl}/users/${userId}/sync-log`, {
      params: httpParams
    });
  }

  getJobStatus(jobId: string): Observable<{ success: boolean; data: KommoJob }> {
    return this.http.get<{ success: boolean; data: KommoJob }>(`${this.baseUrl}/jobs/${jobId}`);
  }

  getRunningJob(): Observable<{ success: boolean; data: KommoJob | null }> {
    return this.http.get<{ success: boolean; data: KommoJob | null }>(`${this.baseUrl}/jobs/running`);
  }
}
